// All data-fetching, polling and mutation logic for the live chat page.
// Behaviour ported unchanged from the old AdminLiveChat.tsx: 3s polling of
// the session list and of the open session, sound + toast alert the first
// time a session goes into waiting_for_admin (tracked in a ref so it never
// fires on first load or repeatedly for the same session), take over, send,
// close. The selected session id lives in the URL (`?s=`) instead of local
// state so a reload keeps the open chat.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  closeChatSession,
  fetchSessionDetail,
  fetchSessions,
  LiveChatApiError,
  playNotificationSound,
  sendChatMessage,
  takeoverSession,
} from './api';
import { ChatMessage, ChatSession, displayName } from './types';

const POLL_MS = 3000;

export function useLiveChat() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('s');

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [closing, setClosing] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const soundEnabledRef = useRef(true);
  const seenWaiting = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const handleAuthError = useCallback(
    (err: unknown) => {
      if (err instanceof LiveChatApiError && err.status === 401) {
        navigate('/admin/login', { replace: true });
        return true;
      }
      return false;
    },
    [navigate]
  );

  const selectSession = useCallback(
    (session: ChatSession | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (session) next.set('s', session.id);
          else next.delete('s');
          return next;
        },
        { replace: true }
      );
      setSelectedSession(session);
      if (!session) setMessages([]);
    },
    [setSearchParams]
  );

  const loadMessages = useCallback(
    async (sessionId: string) => {
      try {
        const data = await fetchSessionDetail(sessionId);
        setMessages(data.messages);
        setSelectedSession(data.session);
      } catch (err) {
        if (!handleAuthError(err)) console.error('Failed to fetch messages:', err);
      }
    },
    [handleAuthError]
  );

  // Self-contained so `loadSessions` below can reference it for the toast
  // action without a circular dependency: it refreshes the list itself
  // rather than calling back into loadSessions.
  const handleTakeover = useCallback(
    async (session?: ChatSession) => {
      const target = session ?? selectedSession;
      if (!target) return;
      setTakingOver(true);
      try {
        await takeoverSession(target.id);
        await loadMessages(target.id);
        try {
          const data = await fetchSessions();
          setSessions(data.sessions);
        } catch {
          // list refresh is best-effort here; the poll loop will catch up
        }
      } catch (err) {
        if (!handleAuthError(err)) console.error('Failed to take over:', err);
      } finally {
        setTakingOver(false);
      }
    },
    [selectedSession, loadMessages, handleAuthError]
  );

  const loadSessions = useCallback(async () => {
    try {
      const data = await fetchSessions();
      const waiting = data.sessions.filter((s) => s.status === 'waiting_for_admin');

      waiting.forEach((session) => {
        if (seenWaiting.current.has(session.id)) return;
        seenWaiting.current.add(session.id);
        if (isFirstLoad.current) return;

        if (soundEnabledRef.current) playNotificationSound();
        toast.warning(`${displayName(session)} wants to talk to someone`, {
          description: session.last_message || session.current_page || undefined,
          duration: 20000,
          action: { label: 'Take over', onClick: () => handleTakeover(session) },
          cancel: { label: 'View', onClick: () => selectSession(session) },
        });
      });

      seenWaiting.current.forEach((id) => {
        const still = data.sessions.find((s) => s.id === id);
        if (!still || still.status !== 'waiting_for_admin') seenWaiting.current.delete(id);
      });

      setSessions(data.sessions);
    } catch (err) {
      if (!handleAuthError(err)) console.error('Failed to fetch sessions:', err);
    }
  }, [handleAuthError, handleTakeover, selectSession]);

  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedSession) return;
    setSendingMessage(true);
    try {
      await sendChatMessage(selectedSession.id, newMessage.trim());
      setNewMessage('');
      await loadMessages(selectedSession.id);
    } catch (err) {
      if (!handleAuthError(err)) console.error('Failed to send message:', err);
    } finally {
      setSendingMessage(false);
    }
  }, [newMessage, selectedSession, loadMessages, handleAuthError]);

  const handleCloseSession = useCallback(async () => {
    if (!selectedSession) return;
    setClosing(true);
    try {
      await closeChatSession(selectedSession.id);
      selectSession(null);
      await loadSessions();
    } catch (err) {
      if (!handleAuthError(err)) console.error('Failed to close session:', err);
    } finally {
      setClosing(false);
    }
  }, [selectedSession, selectSession, loadSessions, handleAuthError]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await loadSessions();
    if (selectedSession) await loadMessages(selectedSession.id);
    setRefreshing(false);
  }, [loadSessions, loadMessages, selectedSession]);

  // Initial load.
  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSessions();
      isFirstLoad.current = false;
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate the open chat from `?s=` on mount, and follow browser back/forward.
  useEffect(() => {
    if (selectedId && selectedId !== selectedSession?.id) {
      loadMessages(selectedId);
    } else if (!selectedId && selectedSession) {
      setSelectedSession(null);
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Poll sessions, and the open session's messages, every 3s.
  useEffect(() => {
    const interval = setInterval(() => {
      loadSessions();
      if (selectedSession) loadMessages(selectedSession.id);
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [loadSessions, loadMessages, selectedSession]);

  return {
    sessions,
    loading,
    refreshing,
    refresh,
    selectedSession,
    messages,
    selectSession,
    soundEnabled,
    toggleSound: () => setSoundEnabled((v) => !v),
    takingOver,
    handleTakeover,
    sendingMessage,
    newMessage,
    setNewMessage,
    handleSendMessage,
    closing,
    handleCloseSession,
  };
}
